import { LayerExtension } from "@deck.gl/core"
import { Coordinate } from "@nova/protobuf"

const MAX_VERTS = 256

export type OrthoMaskExtensionProps = {
    maskPolygon?: number[][] | null
}

export const toMaskPolygon = (maskPolygon: Coordinate[] | null | undefined) =>
    maskPolygon && maskPolygon.length >= 3
        ? maskPolygon.map((corner) => [corner.longitude, corner.latitude] as [number, number])
        : null

// Fragment-only mask extension for BitmapLayer (TileLayer sublayer).
// Avoids the TerrainExtension conflict that MaskExtension causes — no separate
// render pass, no framebuffer interaction; pure per-fragment discard.
//
// Relies on two uniforms already present in BitmapLayer's fragment shader:
//   bounds    — vec4 [west, south, east, north] set by BitmapLayer itself
//   vTexCoord — varying: x∈[0,1] west→east, y∈[0,1] north→south
export class OrthoMaskExtension extends LayerExtension<OrthoMaskExtensionProps> {
    static extensionName = "OrthoMaskExtension"
    static defaultProps: OrthoMaskExtensionProps = {
        maskPolygon: null,
    }
    getShaders() {
        return {
            inject: {
                "fs:#decl": `
uniform vec2 orthoMask_polygon[${MAX_VERTS}];
uniform vec4 orthoMask_tileBounds; // [west, south, east, north] — passed from draw()
uniform int orthoMask_vertCount;

bool orthoMask_contains(vec2 p) {
    bool inside = false;
    int j = orthoMask_vertCount - 1;
    for (int i = 0; i < ${MAX_VERTS}; i++) {
        if (i >= orthoMask_vertCount) break;
        vec2 a = orthoMask_polygon[i];
        vec2 b = orthoMask_polygon[j];
        if ((a.y > p.y) != (b.y > p.y)) {
            float t = (p.y - a.y) / (b.y - a.y);
            if (p.x < a.x + t * (b.x - a.x)) inside = !inside;
        }
        j = i;
    }
    return inside;
}
`,
                // BitmapLayer's built-in `bounds` uniform is [0,0,0,0] when
                // coordinateConversion=0 (standard LNGLAT), so we pass our own.
                // vTexCoord: x∈[0,1] west→east, y∈[0,1] north→south.
                "fs:#main-start": `
if (orthoMask_vertCount > 0) {
    vec2 geoPos = vec2(
        mix(orthoMask_tileBounds.x, orthoMask_tileBounds.z, vTexCoord.x),
        mix(orthoMask_tileBounds.w, orthoMask_tileBounds.y, vTexCoord.y)
    );
    if (!orthoMask_contains(geoPos)) discard;
}
`,
            },
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    draw(this: any) {
        const { maskPolygon, bounds } = this.props
        if (!maskPolygon?.length || !bounds) return

        const flat: number[] = []
        for (const v of maskPolygon) flat.push(v[0], v[1])

        const rawCount = flat.length / 2
        if (rawCount > MAX_VERTS)
            console.warn(
                `OrthoMaskExtension: polygon has ${rawCount} vertices, truncating to ${MAX_VERTS}`
            )
        const vertCount = Math.min(rawCount, MAX_VERTS)
        const padded = new Float32Array(MAX_VERTS * 2)
        padded.set(flat.slice(0, MAX_VERTS * 2))

        const [west, south, east, north] = bounds as number[]

        for (const model of this.getModels()) {
            model.setUniforms({
                orthoMask_polygon: padded,
                orthoMask_tileBounds: [west, south, east, north],
                orthoMask_vertCount: vertCount,
            })
        }
    }
}
